import { useState, useCallback, useRef } from 'react';
import Map, { Source, Layer, NavigationControl, Popup } from 'react-map-gl/maplibre';
import type { MapLayerMouseEvent, MapRef } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { authenticatedMapRequest, INITIAL_VIEW, MAP_STYLE, territoryTileUrl } from '@/config';
import { useMapRecenter } from '@/hooks/useMapRecenter';
import { DataAttribution } from '@/components/map/DataAttribution';
import { useTerritoryStore } from '@/stores/territoryStore';

/**
 * ThermalMap — carte thermique Îlots de chaleur.
 *
 * Source : heatwave_grid (Martin)
 * Propriété affichée : `lst` (Land Surface Temperature, normalisée 0→1).
 *
 * ⚠ INTÉGRITÉ DES DONNÉES :
 * `lst` est un **indice normalisé 0→1**, pas une température absolue en °C.
 * L'interface ne le convertit PAS en °C ; le tooltip affiche uniquement l'indice brut.
 *
 * Rampe : heatwaveRamps.exposition (theme.ts) — vert (#1f9d6b) → rouge (#cc1f1f).
 * En mode "Après simulation", la transparence est réduite pour suggérer un refroidissement
 * (DEMO visuel — les tuiles sous-jacentes ne changent pas).
 */

// Rampe thermique issue de heatwaveRamps.exposition (src/styles/theme.ts)
// Vert #1f9d6b → jaune-vert #cbe04a → jaune #ffd24a → orange #ff9f3c → rouge #ff5a36 → rouge foncé #cc1f1f
const THERMAL_RAMP: unknown[] = [
  'interpolate',
  ['linear'],
  ['get', 'lst'],
  0,    '#1f9d6b',
  0.34, '#cbe04a',
  0.55, '#ffd24a',
  0.72, '#ff9f3c',
  0.88, '#ff5a36',
  1,    '#cc1f1f',
];

export interface ThermalMapProps {
  showAfter: boolean;
  onShowAfterChange: (v: boolean) => void;
  simDone: boolean;
}

interface HoverState {
  lng: number;
  lat: number;
  lst: number;
}

export function ThermalMap({
  showAfter,
  onShowAfterChange,
  simDone,
}: ThermalMapProps) {
  const [hover, setHover] = useState<HoverState | null>(null);
  const territoryCode = useTerritoryStore((s) => s.activeTerritory?.code_insee);
  const mapRef = useRef<MapRef>(null);
  const [mapReady, setMapReady] = useState(false);
  useMapRecenter(mapRef, mapReady);

  const onHover = useCallback((e: MapLayerMouseEvent) => {
    const f = e.features?.[0];
    if (f?.properties) {
      const props = f.properties as Record<string, number>;
      setHover({
        lng: e.lngLat.lng,
        lat: e.lngLat.lat,
        lst: props.lst ?? 0,
      });
    } else {
      setHover(null);
    }
  }, []);

  // En mode "après", on réduit l'opacité pour suggérer une atténuation (DEMO visuel)
  const fillPaint = {
    'fill-color': THERMAL_RAMP as never,
    'fill-opacity': showAfter ? 0.40 : 0.62,
    'fill-outline-color': 'rgba(255,255,255,0.28)',
  };

  if (!territoryCode) return null;

  return (
    <div className="flex flex-col gap-2 h-full">
      {/* ── Titre + bascule Avant/Après ──────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <span className="text-[12.5px] font-semibold text-ink">Carte thermique</span>
        <div className="flex rounded-[9px] border border-line overflow-hidden text-[12px] font-semibold">
          <button
            type="button"
            onClick={() => onShowAfterChange(false)}
            className={`px-3 py-1 transition ${
              !showAfter ? 'bg-ink text-white' : 'bg-white text-muted hover:text-ink'
            }`}
          >
            Avant
          </button>
          <button
            type="button"
            disabled={!simDone}
            onClick={() => onShowAfterChange(true)}
            className={`px-3 py-1 transition ${
              showAfter
                ? 'bg-[#1f8a5b] text-white'
                : 'bg-white text-muted hover:text-ink'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
            title={!simDone ? "Lancez d'abord une simulation" : undefined}
          >
            Après
          </button>
        </div>
      </div>

      {/* ── Carte MapLibre ────────────────────────────────────────────────── */}
      <div className="relative flex-1 min-h-0 overflow-hidden rounded-panel border border-line">
        <Map
          ref={mapRef}
          initialViewState={INITIAL_VIEW}
          mapStyle={MAP_STYLE}
          transformRequest={authenticatedMapRequest}
          style={{ width: '100%', height: '100%' }}
          interactiveLayerIds={['thermal-fill']}
          onLoad={() => setMapReady(true)}
          onMouseMove={onHover}
        >
          <NavigationControl position="top-right" showCompass={false} />
          <DataAttribution />

          {/* Maille thermique heatwave_grid — propriété lst (0→1, non °C) */}
          <Source
            id="thermal"
            type="vector"
            tiles={[territoryTileUrl(territoryCode, 'heatwave_grid')]}
            minzoom={10}
            maxzoom={16}
          >
            <Layer
              id="thermal-fill"
              type="fill"
              source-layer="heatwave_grid"
              paint={fillPaint}
            />
          </Source>

          {hover && (
            <Popup
              longitude={hover.lng}
              latitude={hover.lat}
              closeButton={false}
              offset={12}
            >
              <div className="font-sans text-xs">
                <div className="font-bold text-ink">Exposition thermique</div>
                {/* Indice LST normalisé 0→1 — pas de °C absolus affiché */}
                <div className="font-mono text-muted">
                  Indice LST : {hover.lst.toFixed(2)}
                </div>
              </div>
            </Popup>
          )}
        </Map>

        {/* Badge "Simulation appliquée" en mode Après */}
        {showAfter && (
          <div
            className="absolute bottom-3 left-3 rounded-[8px] px-2.5 py-1 text-[11px] font-semibold"
            style={{ background: 'rgba(31,138,91,.90)', color: '#fff' }}
          >
            Atténuation simulée (DEMO)
          </div>
        )}
      </div>

      {/* ── Légende rampe thermique ───────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-1">
        <span
          className="h-2.5 w-2.5 flex-shrink-0 rounded-sm"
          style={{ background: '#1f9d6b' }}
          aria-hidden="true"
        />
        <span className="text-[11px] text-muted">Fraîche</span>
        <div
          className="flex-1 h-[5px] rounded-full"
          style={{
            background:
              'linear-gradient(to right,#1f9d6b,#cbe04a,#ffd24a,#ff9f3c,#ff5a36,#cc1f1f)',
          }}
          aria-hidden="true"
        />
        <span className="text-[11px] text-muted">Surchauffe</span>
        <span
          className="h-2.5 w-2.5 flex-shrink-0 rounded-sm"
          style={{ background: '#cc1f1f' }}
          aria-hidden="true"
        />
      </div>

      {/* Note d'intégrité des données */}
      <p className="text-[10.5px] text-faint px-1">
        Indice LST 0→1 (non °C absolus) · Source : heatwave_grid (DEMO) ·{' '}
        {showAfter ? 'Vue post-simulation — DEMO visuel' : 'État initial de démonstration'}
      </p>
    </div>
  );
}
